import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHistory, useLocation } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';

import ShareFeedModal from '../../SharingModals/SharedWithMePage/ShareFeedModal';
import ShareFolderModal from '../../SharingModals/SharedWithMePage/ShareFolderModal';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { FOLDER_CONTENT_FILTER } from '../../../enums/ENUMS';
import { FILE_ACCESS_TYPE, FOLDER_TYPE, VIDEOFEED } from '../../../enums/TYPES';
import { getSharedFolderByIdThunk } from '../../../store/videos/videoSlice';
import {
  selectedSharedWithMeFolder,
  switchSharedWithMeModal,
} from '../../../store/folders/foldersSlice';
import { switchSidebar, updateShowWarningModal } from '../../../store/sharedStore/sharedStoreSlice';
import { SharedVideosListHeader } from './sharedVideosListHeader';
import { getFolderContents } from '../../../api/videofeeds';
import LinearProgress from '../../modules/LinearProgress';
import { SortOptions, SortType } from '../../../enums/SORT';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import SearchInput from '../../modules/SearchInput';
import styles from './sharedVideosList.module.scss';
import SharedVideofeed from './SharedVideofeed';
import { getPinTargetType } from '../../../helpers/pinnedContent';
import { getShallowFolder } from '../../../api';
import {
  findSearchValue,
  getSharedWithMePageSortOptionAndType,
  setMailChecker,
  setSharedWithMePageSortOptionAndType,
} from '../../../helpers';
import { PATHS } from '../../../enums';
import { PaginationFooter } from '../../modules/PaginationFooter';
import useDebounce from '../../../helpers/useDebounce';
import { checkContentAvailability } from '../../../api/accessesApi';
import { OrderSelector } from '../../modules/Selector';
import { SharedFolder } from '../../modules/Folders';
import { ContainedButton } from '../../modules/Buttons';
import { useContentDragDrop } from '../../modules/ContentDragDrop/useContentDragDrop';
import PinnedContentSection from '../../modules/PinnedContentSection';

export interface VIDEOFEED_WITH_SIZE extends VIDEOFEED {
  size: number;
  highest_user_access: FILE_ACCESS_TYPE;
  folderAccess: FILE_ACCESS_TYPE[];
}

type FOLDER_CONTENT_ITEM =
  | (FOLDER_TYPE & {
      item_type: 'folder';
      highest_user_access?: FILE_ACCESS_TYPE;
    })
  | (VIDEOFEED_WITH_SIZE & { item_type?: 'videofeed' });

const CONTENT_FILTER_OPTIONS = [
  { label: 'All Items', value: FOLDER_CONTENT_FILTER.ALL, order: SortType.ASC },
  { label: 'Timelines', value: FOLDER_CONTENT_FILTER.TIMELINE, order: SortType.ASC },
  { label: 'Composers', value: FOLDER_CONTENT_FILTER.COMPOSER, order: SortType.ASC },
  { label: 'Subfolders', value: FOLDER_CONTENT_FILTER.SUBFOLDER, order: SortType.ASC },
];

const getContentFilter = (value?: string | null): FOLDER_CONTENT_FILTER => {
  switch (value) {
    case FOLDER_CONTENT_FILTER.TIMELINE:
    case FOLDER_CONTENT_FILTER.COMPOSER:
    case FOLDER_CONTENT_FILTER.SUBFOLDER:
      return value;
    default:
      return FOLDER_CONTENT_FILTER.ALL;
  }
};

const SharedVideosList = () => {
  const [folderContents, setFolderContents] = useState<FOLDER_CONTENT_ITEM[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [filterBy, setFilterBy] = useState<ACCESS_TYPE_ENUM | null>();

  const notification = useSnackbar();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const dispatch = useAppDispatch();

  const page = findSearchValue('page');
  const limit = findSearchValue('limit');
  const sortFromQuery = findSearchValue('sort');
  const directAccess = findSearchValue('directAccess');
  const folderSearch = findSearchValue('search');
  const folderTrail = findSearchValue('folderTrail');
  const breadcrumbAncestorIds = useMemo(
    () =>
      folderTrail
        ? decodeURIComponent(folderTrail).split(',').filter(Boolean)
        : [],
    [folderTrail],
  );

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(SortType.ASC);
  const [sortOption, setSortOption] = useState(SortOptions.NAME);
  const [contentFilter, setContentFilter] = useState<FOLDER_CONTENT_FILTER>(
    () => getContentFilter(searchParams.get('contentFilter')),
  );

  const [folder, setFolder] = useState<FOLDER_TYPE>();
  const [breadcrumbFolders, setBreadcrumbFolders] = useState<FOLDER_TYPE[]>([]);
  const history = useHistory();

  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const [paginationPage, setPaginationPage] = useState(() => {
    const p = findSearchValue('page');
    const n = p ? parseInt(p, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });
  const [paginationLimit, setPaginationLimit] = useState(() => {
    const l = findSearchValue('limit');
    const n = l ? parseInt(l, 10) : 10;
    return Number.isFinite(n) && n > 0 ? n : 10;
  });
  const [total, setTotal] = useState(0);
  const { currentUser } = useAppSelector((state) => state.user);

  useEffect(() => {
    setContentFilter(getContentFilter(searchParams.get('contentFilter')));
  }, [location.search]);

  const runCheck = async (type: 'folder' | 'videofeed', id: string) => {
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText:
              'The content you are trying to access has been deleted',
            redirect: true,
          }),
        );
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText:
              'You do not have permission to access the content you are trying to view.',
            redirect: true,
          }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName
                ? ` The content is from ${res.data.organizationName}.`
                : ''
            }`,
            redirect: true,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFolder = async (id: string) => {
    try {
      runCheck('folder', id);
      const res = await getShallowFolder(id);
      if (currentUser && +res?.data?.owner?.id === +currentUser?.id) {
        history.replace(
          `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${id}&parentPage=${PATHS.MY_FOLDERS.path}`,
        );
        return;
      }
      setFolder(res.data);
    } catch (err: any) {
      console.log(err);
    }
  };

  useEffect(() => {
    let active = true;

    const loadBreadcrumbFolders = async () => {
      if (!breadcrumbAncestorIds.length) {
        setBreadcrumbFolders([]);
        return;
      }

      try {
        const responses = await Promise.all(
          breadcrumbAncestorIds.map((id) => getShallowFolder(id)),
        );

        if (active) {
          setBreadcrumbFolders(responses.map((response) => response.data));
        }
      } catch (err) {
        if (active) {
          setBreadcrumbFolders([]);
        }
      }
    };

    loadBreadcrumbFolders();

    return () => {
      active = false;
    };
  }, [breadcrumbAncestorIds]);

  useEffect(() => {
    dispatch(switchSidebar(false));
    if (sort === SortType.ASC) {
      setSort(SortType.ASC);
    } else if (sort === SortType.DESC) {
      setSort(SortType.DESC);
    }
  }, [sortFromQuery, dispatch]);

  const fetchFolderContents = async (
    sortFromArg?: SortType,
    sortOpinionFromArg?: SortOptions,
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setLoading(true);

    try {
      if (folderId && folder?.id !== folderId) {
        await fetchFolder(folderId);
      }
      const sortOptionObject = getSharedWithMePageSortOptionAndType();

      const activeSort = sortOptionObject
        ? (sortOptionObject.split('/')[0] as SortType)
        : sortFromArg || sort;
      const activeSortOption = sortOptionObject
        ? (sortOptionObject.split('/')[1] as SortOptions)
        : sortOpinionFromArg || sortOption;

      const res = await getFolderContents({
        folderId: folderId as string,
        sort: activeSort,
        sortOption: activeSortOption,
        accessType: filterBy ? filterBy : null,
        contentFilter,
        page: paginationPage,
        limit: paginationLimit,
        search,
        view: 'shared',
        signal: abortController.signal,
      });
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setFolderContents(res.documents);
      setTotal(res.total);

      if (folderId && page && limit) {
        await dispatch(
          getSharedFolderByIdThunk({
            folderId,
            page: +page,
            limit: +limit,
            sort: activeSort,
            search: folderSearch || '',
            sortOption: activeSortOption,
            accessType: filterBy ? [filterBy] : undefined,
          }),
        );
      }
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        return;
      }
      if (err?.response?.data?.message) {
        notification.showMessage(err.response.data.message);
        history.push(PATHS.SHARED_WITH_ME.path);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const debouncedSearch = useDebounce(search, 1000);
  const urlParams = document.location.search;

  useEffect(() => {
    const res = getSharedWithMePageSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      setSort(sortAndOption[0] as SortType);
      setSortOption(sortAndOption[1] as SortOptions);
      fetchFolderContents(
        sortAndOption[0] as SortType,
        sortAndOption[1] as SortOptions,
      );
    } else {
      fetchFolderContents();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    urlParams,
    sort,
    sortOption,
    filterBy,
    contentFilter,
    debouncedSearch,
    paginationLimit,
    paginationPage,
  ]);

  const searchContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    onPaginate(null, 0);
  };

  const sortFiltering = (value: ACCESS_TYPE_ENUM) => {
    if (value === filterBy || value === ACCESS_TYPE_ENUM.ALL) {
      setFilterBy(null);
    } else {
      setFilterBy(value);
    }
    onPaginate(null, 0);
  };

  const onContentFilterSelect = (
    _: any,
    option?: { label: string; value: string; order: SortType },
  ) => {
    if (!option) return;
    const nextFilter = option.value as FOLDER_CONTENT_FILTER;
    const nextSearch = new URLSearchParams(location.search);

    nextSearch.set('page', '0');
    if (nextFilter === FOLDER_CONTENT_FILTER.ALL) {
      nextSearch.delete('contentFilter');
    } else {
      nextSearch.set('contentFilter', nextFilter);
    }

    history.replace(
      `${location.pathname}${nextSearch.toString() ? `?${nextSearch.toString()}` : ''}`,
    );
    setContentFilter(nextFilter);
    onPaginate(null, 0);
  };

  const sortHandler = (
    _: any,
    option?: { label: string; value: string; order: SortType },
  ) => {
    if (!option) return;
    if (sortOption !== option.value) {
      setSort(option.order);
      setSharedWithMePageSortOptionAndType(
        option.order,
        option.value as SortOptions,
      );
      setSortOption(option.value as SortOptions);
    } else if (sortOption === option.value) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setSort(sortT);
      setSharedWithMePageSortOptionAndType(sortT, option.value as SortOptions);
    }
    onPaginate(null, 0);
  };

  const redirectToSharedPage = () => {
    history.push(PATHS.SHARED_WITH_ME.path);
  };

  const onBreadcrumbClick = (
    targetFolderId?: string,
    ancestorIndex?: number,
  ) => {
    if (!targetFolderId) {
      redirectToSharedPage();
      return;
    }

    setPaginationPage(0);

    const nextTrail =
      typeof ancestorIndex === 'number' && ancestorIndex > 0
        ? breadcrumbAncestorIds.slice(0, ancestorIndex).join(',')
        : '';

    history.push(
      `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${targetFolderId}&parentPage=${PATHS.SHARED_WITH_ME.label}&page=0&limit=${paginationLimit}&sort=${sort}&search=${search}${
        nextTrail ? `&folderTrail=${encodeURIComponent(nextTrail)}` : ''
      }${
        contentFilter !== FOLDER_CONTENT_FILTER.ALL
          ? `&contentFilter=${contentFilter}`
          : ''
      }`,
    );
  };

  const onHierarchyBackClick = () => {
    const parentFolderId =
      breadcrumbAncestorIds[breadcrumbAncestorIds.length - 1];
    if (!parentFolderId) {
      onBreadcrumbClick();
      return;
    }

    onBreadcrumbClick(
      String(parentFolderId),
      breadcrumbAncestorIds.length - 1,
    );
  };

  const onNameSort = () => {
    const sortT = sortOption === SortOptions.NAME && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.NAME);
    setSortOption(SortOptions.NAME);
    onPaginate(null, 0);
  };

  const onSizeSort = () => {
    const sortT = sortOption === SortOptions.SIZE && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.SIZE);
    setSortOption(SortOptions.SIZE);
    onPaginate(null, 0);
  };

  const onOwnerSort = () => {
    const sortT = sortOption === SortOptions.OWNER && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.OWNER);
    setSortOption(SortOptions.OWNER);
    onPaginate(null, 0);
  };

  useEffect(() => {
    if (directAccess) {
      notification.showMessage('Oops.. seems the video was deleted');
    }
  }, [directAccess, notification]);

  const onPaginate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaginationPage(newPage);
  };

  const onRowsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaginationPage(0);
    setPaginationLimit(parseInt(e.target.value, 10));
  };

  const videofeedsList = useMemo(
    () =>
      folderContents.filter(
        (item) => item.item_type !== 'folder',
      ) as VIDEOFEED_WITH_SIZE[],
    [folderContents],
  );

  const orderedFeedsForIndex = useMemo(() => {
    const ordered: VIDEOFEED_WITH_SIZE[] = [];
    const seen = new Set<string>();

    const append = (feed?: VIDEOFEED_WITH_SIZE) => {
      if (!feed) return;
      const id = String(feed.id);
      if (seen.has(id)) return;
      seen.add(id);
      ordered.push(feed);
    };

    videofeedsList.forEach((feed) => {
      append(feed);
      (feed.reference_timelines as VIDEOFEED_WITH_SIZE[] | undefined)?.forEach(
        (timeline) => append(timeline),
      );
    });

    return ordered;
  }, [videofeedsList]);

  const feedIndexById = useMemo(
    () =>
      orderedFeedsForIndex.reduce<Record<string, number>>((acc, feed, index) => {
        acc[String(feed.id)] = index;
        return acc;
      }, {}),
    [orderedFeedsForIndex],
  );

  const nextFolderTrail = useMemo(() => {
    const trailParts = folderTrail
      ? decodeURIComponent(folderTrail).split(',').filter(Boolean)
      : [];
    if (folderId && trailParts[trailParts.length - 1] !== folderId) {
      trailParts.push(folderId);
    }

    return trailParts.join(',');
  }, [folderId, folderTrail]);

  const pinnedItems = paginationPage === 0
    ? folderContents.filter((item) => Boolean((item as any)?.is_pinned))
    : [];
  const regularItems = paginationPage === 0
    ? folderContents.filter((item) => !Boolean((item as any)?.is_pinned))
    : folderContents;

  const drag = useContentDragDrop<FOLDER_CONTENT_ITEM>({
    items: folderContents,
    contextType: 'shared_with_me',
    contextParentId: folderId,
    firstPage: paginationPage === 0,
    targetType: getPinTargetType,
    refresh: () => { if (paginationPage !== 0) setPaginationPage(0); else fetchFolderContents(); },
    disabled: loading,
  });

  const renderContentRow = (item: FOLDER_CONTENT_ITEM) => item.item_type === 'folder' ? (
    <div
      key={`folder-${item.id}`}
      className={styles.folderRowWrapper}
      {...drag.rowProps(item)}
    >
      {drag.indicator(item)}
      <SharedFolder
        data={{
          ...item,
          access: item.highest_user_access
            ? [item.highest_user_access as any]
            : [],
          videofeeds: [],
        } as any}
        page={paginationPage}
        limit={paginationLimit}
        sort={sort}
        search={search}
        setModalType={() => {}}
        fetchType={null}
        layout="nested"
        dateSortOption={sortOption}
        onOpen={() => {
          if (String(item.id) === String(folderId)) {
            return;
          }

          history.push(
            `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${item.id}&parentPage=${PATHS.SHARED_WITH_ME.label}&page=${paginationPage}&limit=${paginationLimit}&sort=${sort}&search=${search}${
              nextFolderTrail
                ? `&folderTrail=${encodeURIComponent(nextFolderTrail)}`
                : ''
            }${
              contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
        }}
        onShare={
          item?.highest_user_access &&
          [
            ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE,
            ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE,
          ].includes(
            item.highest_user_access.access.title as ACCESS_TYPE_ENUM,
          )
            ? () => {
                dispatch(
                  selectedSharedWithMeFolder({
                    ...item,
                    access: item.highest_user_access
                      ? [item.highest_user_access as any]
                      : [],
                    videofeeds: [''],
                  } as any),
                );
                dispatch(switchSharedWithMeModal(true));
              }
            : undefined
        }
        pinContextType="shared_with_me"
        pinContextParentId={folderId}
        onPinUpdated={fetchFolderContents}
      />
    </div>
  ) : (
    <div
      key={`feed-${item.id}`}
      {...drag.rowProps(item)}
    >
      {drag.indicator(item)}
      <SharedVideofeed
        feed={item}
        i={feedIndexById[String(item.id)] || 0}
        folderId={folder?.id}
        referenceTimelines={
          (item.reference_timelines as
            | VIDEOFEED_WITH_SIZE[]
            | undefined) || []
        }
        feedIndexById={feedIndexById}
        forceMovieIcon={
          Boolean(item.is_collection_visual) ||
          (item.reference_timelines?.length || 0) > 0
        }
        pinContextType="shared_with_me"
        pinContextParentId={folderId}
        onPinUpdated={fetchFolderContents}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>
          <span>File Management / </span>
          <button
            type="button"
            className={styles.breadcrumbButton}
            onClick={() => onBreadcrumbClick()}
          >
            Shared With Me
          </button>
          {breadcrumbFolders.map((breadcrumbFolder, index) => (
            <React.Fragment key={breadcrumbFolder.id}>
              <span className={styles.breadcrumbSeparator}> / </span>
              <button
                type="button"
                className={styles.breadcrumbButton}
                onClick={() =>
                  onBreadcrumbClick(String(breadcrumbFolder.id), index)
                }
              >
                {breadcrumbFolder.name}
              </button>
            </React.Fragment>
          ))}
          {folder?.name && (
            <>
              <span className={styles.breadcrumbSeparator}> / </span>
              <span className={styles.breadcrumbCurrent}>{folder.name}</span>
            </>
          )}
        </div>
        <div className={styles.pageTitle}>
          {!!folderId && (
            <ContainedButton
              kind="purple"
              className={styles.backButton}
              onClick={onHierarchyBackClick}
            >
              <span className={styles.backButtonContent}>
                <ArrowBack fontSize="small" />
                Back
              </span>
            </ContainedButton>
          )}
          <span>{folder?.name}</span>
        </div>
      </div>

      {(loading || drag.busy) && <LinearProgress />}

      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={searchContent} className={styles.search} />
        <div className={styles.sortToolbarSelector}>
          <OrderSelector
            title={'Sorting by'}
            selectedValue={sortOption}
            selectedOrder={sort}
            options={[
              { label: 'A-Z ↓', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A ↑', value: SortOptions.NAME, order: SortType.DESC },
              { label: 'Owner ↓', value: SortOptions.OWNER, order: SortType.DESC },
              { label: 'Owner ↑', value: SortOptions.OWNER, order: SortType.ASC },
              { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
              { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
              {
                label: 'Last viewed ↑',
                value: SortOptions.LAST_VIEWED,
                order: SortType.ASC,
              },
              {
                label: 'Last viewed ↓',
                value: SortOptions.LAST_VIEWED,
                order: SortType.DESC,
              },
              {
                label: 'Last Modified ↑',
                value: SortOptions.UPDATED_AT,
                order: SortType.ASC,
              },
              {
                label: 'Last Modified ↓',
                value: SortOptions.UPDATED_AT,
                order: SortType.DESC,
              },
              {
                label: 'Created At ↑',
                value: SortOptions.CREATED_AT,
                order: SortType.ASC,
              },
              {
                label: 'Created At ↓',
                value: SortOptions.CREATED_AT,
                order: SortType.DESC,
              },
            ]}
            onSelect={sortHandler}
          />
        </div>

        <div className={styles.sortToolbar2Selector}>
          <select
            id="filterBy"
            value={filterBy || ACCESS_TYPE_ENUM.ALL}
            disabled={contentFilter === FOLDER_CONTENT_FILTER.SUBFOLDER}
            onChange={(e) => sortFiltering(e.target.value as ACCESS_TYPE_ENUM)}
          >
            <option value={ACCESS_TYPE_ENUM.ALL}>All</option>
            <option value={ACCESS_TYPE_ENUM.VIEW}>View</option>
            <option value={ACCESS_TYPE_ENUM.VIEW_AND_COMMENT}>
              View &amp; Comment
            </option>
            <option value={ACCESS_TYPE_ENUM.COMMENT_AND_CREATE}>
              Comment &amp; Create
            </option>
            <option value={ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE}>
              Comment, Create &amp; Reshare
            </option>
            <option value={ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE}>
              Edit &amp; Reshare
            </option>
            <option value={ACCESS_TYPE_ENUM.WITHOUT_ACCESS}>Mixed</option>
          </select>
        </div>

        <div className={styles.filterToolbar}>
          <OrderSelector
            title={'Filter by'}
            selectedValue={contentFilter}
            selectedOrder={SortType.ASC}
            options={CONTENT_FILTER_OPTIONS}
            onSelect={onContentFilterSelect}
          />
        </div>
      </div>

      <SharedVideosListHeader
        onDateSort={sortHandler}
        sort={sort}
        sortOption={sortOption}
        onNameClick={onNameSort}
        onSizeClick={onSizeSort}
        onOwnerClick={onOwnerSort}
      />

      <div className={styles.list}>
        {(pinnedItems.length > 0 || drag.active) && (
          <div key="pinned-content" {...drag.zoneProps(true)}>
            <PinnedContentSection count={pinnedItems.length}>
              {pinnedItems.map(renderContentRow)}
              {drag.emptyHint(true, pinnedItems.length === 0)}
            </PinnedContentSection>
          </div>
        )}
        <div key="regular-content" {...drag.zoneProps(false)}>
          {regularItems.map(renderContentRow)}
          {drag.emptyHint(false, regularItems.length === 0)}
        </div>
      </div>

      <ShareFeedModal refetchFeeds={fetchFolderContents} />
      <ShareFolderModal />

      <PaginationFooter
        total={total}
        page={paginationPage}
        onPageChange={onPaginate}
        onRowsPerPageChange={onRowsChange}
        rowsPerPage={paginationLimit}
        hideVersion
        pinnedCount={folderContents.filter((item: any) => item?.is_pinned).length}
      />
    </div>
  );
};

export default SharedVideosList;
